/**
 * NZ wall-clock helpers, shared by the Worker (Cloudflare V8 has full Intl
 * support) and the SPAs (browsers do too).
 *
 * Why this exists: `sessions.session_date`, `start_time`, `end_time` are stored
 * as NZ wall-clock with no timezone — that's how clubs schedule ("06:15 Mon").
 * Comparing those against `new Date()` directly gets you UTC, which is 12 or
 * 13 hours adrift. Going through Intl with the IANA zone handles the
 * NZST/NZDT rollover automatically.
 */

export const NZ_TZ = 'Pacific/Auckland';

export interface NzWallClock {
  /** YYYY-MM-DD in NZ. */
  isoDate: string;
  /** HH:MM:SS in NZ (24h). */
  isoTime: string;
  /** Minutes since 00:00 NZ on isoDate. Useful for time-of-day comparisons. */
  totalMinutes: number;
}

export function nzWallClock(now: Date = new Date()): NzWallClock {
  const fmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: NZ_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
  const parts = fmt.formatToParts(now);
  const get = (t: string): string => parts.find((p) => p.type === t)?.value ?? '00';
  // en-CA returns 24 for midnight in some Node versions; normalise.
  const hh = get('hour') === '24' ? '00' : get('hour');
  const mm = get('minute');
  const ss = get('second');
  return {
    isoDate: `${get('year')}-${get('month')}-${get('day')}`,
    isoTime: `${hh}:${mm}:${ss}`,
    totalMinutes: parseInt(hh, 10) * 60 + parseInt(mm, 10),
  };
}

/** NZ wall-clock hour (0–23) in Pacific/Auckland (NZST / NZDT). */
export function nzHourOfDay(now: Date = new Date()): number {
  return parseInt(nzWallClock(now).isoTime.slice(0, 2), 10);
}

/**
 * Today's date in NZ + N days. Date arithmetic is timezone-agnostic once we
 * have the NZ-anchored start date, so we can use plain UTC math from there.
 */
export function nzDatePlusDays(n: number, from: Date = new Date()): string {
  const fromIso = nzWallClock(from).isoDate;
  return addDaysIso(fromIso, n);
}

/** Add days to a YYYY-MM-DD string. Pure date math; no timezone in or out. */
export function addDaysIso(date: string, days: number): string {
  const [y, m, d] = date.split('-').map((s) => parseInt(s, 10));
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + days);
  const yy = dt.getUTCFullYear();
  const mm = String(dt.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(dt.getUTCDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
}

/** Day-of-week (0=Sun..6=Sat) for a YYYY-MM-DD string. */
export function dayOfWeekIso(date: string): number {
  const [y, m, d] = date.split('-').map((s) => parseInt(s, 10));
  return new Date(Date.UTC(y, m - 1, d)).getUTCDay();
}

/** Parse "HH:MM" or "HH:MM:SS" → minutes-since-midnight. */
export function timeToMinutes(t: string): number {
  const [h, m] = t.split(':');
  return parseInt(h, 10) * 60 + parseInt(m, 10);
}

/** Display stored session time ("06:15:00") as 12-hour NZ clock, e.g. "6:15 am". */
export function formatClock12h(time: string | null | undefined): string {
  if (!time) return '—';
  const [hStr, mStr] = time.trim().split(':');
  const hour = Number.parseInt(hStr ?? '', 10);
  const minute = Number.parseInt((mStr ?? '00').slice(0, 2), 10);
  if (!Number.isFinite(hour) || !Number.isFinite(minute)) return time.slice(0, 5) || '—';
  const ampm = hour >= 12 ? 'pm' : 'am';
  const h12 = hour % 12 || 12;
  return `${h12}:${String(minute).padStart(2, '0')} ${ampm}`;
}

export function formatClockRange12h(
  start: string | null | undefined,
  end: string | null | undefined,
): string {
  if (!start && !end) return '—';
  if (!end) return formatClock12h(start);
  if (!start) return formatClock12h(end);
  return `${formatClock12h(start)} – ${formatClock12h(end)}`;
}

const NZ_TIME_12H: Intl.DateTimeFormatOptions = {
  timeZone: NZ_TZ,
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
};

/** Clock time for an instant (ISO / Date), NZ 12-hour, e.g. "3:45 pm". */
export function formatInstantTime12h(at: Date | string | number = new Date()): string {
  const d = at instanceof Date ? at : new Date(at);
  if (Number.isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat('en-NZ', NZ_TIME_12H).format(d);
}

/** Date + time for an instant, NZ 12-hour, e.g. "18 Aug, 3:45 pm". */
export function formatInstantDateTime12h(
  at: Date | string | number,
  opts?: { includeYear?: boolean },
): string {
  const d = at instanceof Date ? at : new Date(at);
  if (Number.isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat('en-NZ', {
    timeZone: NZ_TZ,
    day: 'numeric',
    month: 'short',
    ...(opts?.includeYear ? { year: 'numeric' as const } : {}),
    ...NZ_TIME_12H,
  }).format(d);
}

/** Normalise "HH:MM" / "HH:MM:SS" → "HH:MM:SS". */
export function normaliseTimeOfDay(time: string): string {
  const parts = time.trim().split(':');
  const hh = (parts[0] ?? '00').padStart(2, '0');
  const mm = (parts[1] ?? '00').padStart(2, '0');
  const ss = (parts[2] ?? '00').padStart(2, '0');
  return `${hh}:${mm}:${ss.slice(0, 2)}`;
}

/**
 * Convert an NZ wall-clock date + time to a UTC `Date`.
 * Sessions are stored without TZ; delivery scheduling needs an absolute instant.
 */
export function nzWallDateTimeToUtc(isoDate: string, time: string, now: Date = new Date()): Date {
  const wallTime = normaliseTimeOfDay(time);
  const targetMin =
    Date.parse(`${isoDate}T00:00:00Z`) / 60000 + timeToMinutes(wallTime);

  // Seed near the NZ day, then converge using wall-clock formatting.
  let guess = Date.parse(`${isoDate}T${wallTime}.000Z`) - 12 * 3600e3;
  if (!Number.isFinite(guess)) guess = now.getTime();

  for (let i = 0; i < 6; i++) {
    const wall = nzWallClock(new Date(guess));
    const actualMin =
      Date.parse(`${wall.isoDate}T00:00:00Z`) / 60000 + wall.totalMinutes;
    const deltaMin = targetMin - actualMin;
    if (Math.abs(deltaMin) < 0.05) break;
    guess += deltaMin * 60_000;
  }
  return new Date(guess);
}
